import type { ChannelSlice } from '../ChannelSlice';
import type { ExecutionsWidgetAdapter } from '../ExecutionsWidgetAdapter';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { ColumnTemplate } from '@thinkalpha/platform-ws-client/contracts/column-template.js';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { Observable } from 'rxjs';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import type { TableStatus } from 'src/features/table/types';
import { enableTableColumnFilters } from 'src/lib/featureFlags/flags/enableTableColumnFilters';
import { parseInstrument } from 'src/lib/instrumentUtils';
import type { ColumnFiltersModel } from 'src/models/ColumnFiltersModel';
import type { ColumnFiltersEventBus, TableColumnFilters } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import { templateColumnFilters } from 'src/models/ColumnFiltersModel/templateColumnFilters';
import type {
    ColumnFilterTemplateColumns,
    TemplateColumnFilters,
} from 'src/models/ColumnFiltersModel/templateColumnFilters';
import type { ExecutionsWidgetModel } from 'src/models/ExecutionsWidgetModel';
import type { TableConstructionProgress } from 'src/models/TableModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal, FormattableGridSettingsSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

@injectable()
export class ExecutionsWidgetModelImpl extends ReactiveInjectable implements ExecutionsWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #instrument = signal<Instrument>(parseInstrument(''));
    #accountIds: Signal<string[]> = signal([]);
    #platforms: Signal<string[]> = signal([]);
    #isAccountSelectionValid: Signal<boolean> = signal(true);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);
    #columnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #tableRowCount: Signal<number> = signal(0);
    #tableRowCountObservable: Signal<Observable<number> | undefined> = signal(undefined);
    #tableTotalRowCount: Signal<number | null> = signal(null);
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        failureCount: 0,
        progress: undefined,
    });
    #refetchCallback: (() => void) | null = null;

    headerElements!: WidgetHeaderElement[];

    eventBus!: ExecutionsWidgetModel['eventBus'];

    #stickyFilterSlice: StickyFilterSlice;

    readonly #columnFilters: TemplateColumnFilters;

    get columnFilterState(): ColumnFiltersModel {
        return this.#columnFilters.state;
    }

    get columnFilters(): TableColumnFilters {
        return this.#columnFilters.views;
    }

    setColumnFilterColumns(columns: ColumnFilterTemplateColumns): void {
        this.#columnFilters.setColumns(columns);
    }

    constructor(
        @inject('ExecutionsWidgetAdapter') private adapter: ExecutionsWidgetAdapter,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('FeatureFlagService') featureFlagService: Pick<ReactBindings['FeatureFlagService'], 'getSignal'>,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        // Session-only column filters (ADR 0014); kept out of adapter persistence.
        this.#columnFilters = templateColumnFilters(featureFlagService.getSignal(enableTableColumnFilters));
        this.disposableStack.use(this.#columnFilters.state);

        this.eventBus = this.#buildEventBus();
    }

    get instrument(): ReadonlySignal<Instrument> {
        return this.#instrument as ReadonlySignal<Instrument>;
    }

    setInstrument(instrument: Instrument) {
        this.adapter.persist({ instrument });
        this.#instrument.value = instrument;
    }

    userSelectedSymbol(symbol: string) {
        this.adapter.userSelectedSymbol(symbol);
    }

    #buildEventBus(): ExecutionsWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Events
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.adapter.persist({ accountIds: payload.accountIds });
                    this.#accountIds.value = payload.accountIds;
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    this.adapter.persist({ accountIds: [payload.accountId] });
                    this.#accountIds.value = [payload.accountId];
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::updateSelectedPlatforms', (payload) => {
                    this.adapter.persist({ platforms: payload.platformIds });
                    this.#platforms.value = payload.platformIds;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.columnTemplate });
                    this.#columnTemplate.value = payload.columnTemplate;
                })
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.templateId });
                    this.#columnTemplate.value = payload.templateId;
                })
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                // Signals
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => this.#accountIds.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::selectedPlatforms', () =>
                    computed(() => this.#platforms.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal(
                    'widget::formattableGridSettings',
                    () =>
                        computed(() => ({
                            settings: null,
                            defaults: null,
                        })) satisfies FormattableGridSettingsSignal[keyof FormattableGridSettingsSignal],
                )
                .addSignal('widget::selectedColumnTemplate', () => this.#columnTemplate)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableRowCountObservable', () => this.#tableRowCountObservable)
                .addSignal('widget::tableTotalRowCount', () => this.#tableTotalRowCount)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchCallback?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus)
            .merge<ColumnFiltersEventBus>(this.#columnFilters.views.eventBus);
    }

    init(tabId?: string) {
        // Always initialize slices — use a generated UUID for standalone mode so signals are available
        const effectiveTabId = tabId ?? crypto.randomUUID();
        this.#channelSlice.init(effectiveTabId);
        this.#widgetTabSlice.init(effectiveTabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        if (!tabId) {
            (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value =
                t('widgets::executions::name');
        }

        // Initialize StickyFilterSlice with the shared adapter
        this.#stickyFilterSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        // Sync current adapter state into signals
        this.#syncFromAdapter();

        this.headerElements = createExecutionsWidgetHeaderElements({ isStandalone: !tabId });
    }

    #syncFromAdapter() {
        const state = this.adapter.getState();
        this.#instrument.value = state.instrument;
        this.#accountIds.value = state.accountIds;
        this.#platforms.value = state.platforms;
        this.#columnTemplate.value = state.columnTemplate;
    }

    setTableRowCount(count: number) {
        this.#tableRowCount.value = count;
    }

    setTableRowCountObservable(observable: Observable<number> | undefined) {
        this.#tableRowCountObservable.value = observable;
    }

    setTableTotalRowCount(count: number | null) {
        this.#tableTotalRowCount.value = count;
    }

    updateTableConnectionStatus(connectionStatus: {
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }): void {
        this.#connectionStatus.value = connectionStatus;
    }

    setRefetchCallback(fn: (() => void) | null): void {
        this.#refetchCallback = fn;
    }
}

export function createExecutionsWidgetHeaderElements({ isStandalone = false }: { isStandalone?: boolean } = {}) {
    const baseElements = isStandalone ? STANDALONE_COMMON_HEADER_ELEMENTS : COMMON_HEADER_ELEMENTS;

    return makeHeaderElements<ExecutionsWidgetModel['eventBus']>([
        ...baseElements,
        // Left side (before spacer at position 50)
        { type: 'stickyFilter', position: 25, priority: 70 },
        { type: 'columnFilters', position: 25.5, priority: 90 },
        // Right side (after spacer at position 50)
        { type: 'accounts', priority: 60, position: 60, selectType: 'multi', allAccountsLabel: 'all-accounts' },
        { type: 'platforms', position: 65, priority: 50 },
        { type: 'columnTemplateMenu', position: 70, priority: 20, columnTemplateKey: 'Execution' },
        { type: 'tableRowCount', position: 75, priority: 40 },
        {
            type: 'connectionStatus',
            position: 76,
            priority: 30,
            overflowPosition: 5,
        },
    ]);
}
